import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Camera, CheckCircle2, FileText, Gauge, Loader2, Search, Wrench, Car } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AlertTriangle } from "lucide-react";
import { LiveCamera } from "../components/LiveCamera";
import { api, API_BASE } from "../lib/api";
import { WsReceive, WsOrders } from "../components/WorkshopOrders";

const KINDS = [["oil", "تغيير زيت"], ["parts", "قطع غيار"], ["both", "زيت + قطع"]];

const PhotoBox = ({ label, icon: Icon, kind, token, file, onDone, testid }) => {
  const [busy, setBusy] = useState(false);
  const [cam, setCam] = useState(false);
  const pick = async (f) => {
    if (!f) return;
    setBusy(true);
    try { const r = await api.workshopUpload(token, kind, f); onDone({ ...r, preview: URL.createObjectURL(f) }); if (r.value == null) toast.info("ما قدرنا نقرأ الرقم من الصورة، اكتبه يدوياً"); }
    catch (er) { toast.error(er?.response?.data?.detail || "تعذّر رفع الصورة"); }
    finally { setBusy(false); }
  };
  return (
    <>
    <button type="button" onClick={() => !busy && setCam(true)} className={`relative flex h-32 w-full cursor-pointer flex-col items-center justify-center gap-1.5 overflow-hidden rounded-2xl border-2 border-dashed text-sm transition-colors ${file ? "border-emerald-300 bg-emerald-50" : "border-slate-300 bg-white hover:border-blue-400"}`} data-testid={testid}>
      {file ? <img src={file.preview} alt={label} className="absolute inset-0 h-full w-full object-cover opacity-80" /> : null}
      <span className="relative z-10 flex flex-col items-center gap-1 rounded-xl bg-white/85 px-3 py-1.5 font-semibold text-slate-700">
        {busy ? <Loader2 className="h-5 w-5 animate-spin text-blue-600" /> : file ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Icon className="h-5 w-5 text-blue-600" />}
        {busy ? "جاري القراءة..." : file ? "إعادة التصوير" : label}
      </span>
    </button>
    <LiveCamera open={cam} title={label} onClose={() => setCam(false)} onShot={pick} />
    </>
  );
};

const VehiclePick = ({ token, value, onChange, custodyOnly }) => {
  const [q, setQ] = useState(""); const [list, setList] = useState([]);
  useEffect(() => {
    if (value || !q.trim()) { setList([]); return; }
    const t = setTimeout(() => api.workshopVehicles(token, q, custodyOnly).then(setList).catch(() => setList([])), 250);
    return () => clearTimeout(t);
  }, [q, token, value]);
  if (value) return (
    <div className="flex items-center justify-between rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5" data-testid="ws-vehicle-selected">
      <span className="flex items-center gap-2 font-bold text-blue-800"><Car className="h-4 w-4" /> {value}</span>
      <button className="text-xs text-slate-500 underline" onClick={() => { onChange(""); setQ(""); }} data-testid="ws-vehicle-change">تغيير</button>
    </div>
  );
  return (
    <div className="relative">
      <Search className="absolute right-3 top-3 h-4 w-4 text-slate-400" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="اكتب رقم اللوحة..." className="h-11 pr-9" data-testid="ws-vehicle-search" />
      {q.trim() && (
        <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
          {list.length === 0 ? <div className="p-3 text-xs text-slate-400">لا توجد مركبة مطابقة</div> : list.map((v) => (
            <button key={v.vehicle_number} onClick={() => onChange(v.vehicle_number)} className="flex w-full items-center justify-between border-b border-slate-50 px-3 py-2.5 text-right text-sm hover:bg-slate-50" data-testid={`ws-vehicle-opt-${v.vehicle_number}`}>
              <span className="font-semibold">{v.vehicle_number}</span><span className="text-xs text-slate-400">{v.driver || ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const History = ({ token, vn, onLoad }) => {
  const [h, setH] = useState(null);
  useEffect(() => { setH(null); api.workshopHistory(token, vn).then((d) => { setH(d); onLoad?.(d); }).catch(() => {}); }, [token, vn]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!h) return null;
  const n = (x) => Number(x || 0).toLocaleString("en");
  return (
    <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3 text-xs" data-testid="ws-history">
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-slate-50 p-2"><div className="text-slate-400">آخر تغيير زيت</div><div className="mt-0.5 font-bold text-slate-700" data-testid="ws-last-oil">{h.last_oil ? `${n(h.last_oil.odometer)} كم · ${h.last_oil.date}` : "لا يوجد"}</div></div>
        <div className="rounded-lg bg-blue-50 p-2"><div className="text-slate-400">التغيير القادم عند</div><div className="mt-0.5 font-bold text-blue-700" data-testid="ws-next-due">{h.next_due ? `${n(h.next_due)} كم` : "—"}</div></div>
      </div>
      {h.items.length > 0 && <div className="space-y-1">{h.items.map((it, i) => (
        <div key={i} className="flex justify-between border-t border-slate-50 pt-1 text-slate-500"><span>{it.kind}</span><span className="flex gap-3"><span>{it.odometer ? `${n(it.odometer)} كم` : ""}</span><span dir="ltr" className="font-mono-nums">{it.date}</span></span></div>
      ))}</div>}
    </div>
  );
};

const EarlyDialog = ({ info, onClose }) => {
  const [left, setLeft] = useState(5);
  useEffect(() => { if (!info) return; setLeft(5); const t = setInterval(() => setLeft((x) => (x > 0 ? x - 1 : 0)), 1000); return () => clearInterval(t); }, [info]);
  const n = (x) => Number(x || 0).toLocaleString("en");
  return (
    <Dialog open={!!info} onOpenChange={(o) => { if (!o && left === 0) onClose(); }}>
      <DialogContent dir="rtl" className="max-w-sm border-rose-200 font-body tracking-normal [&_*]:tracking-normal [&>button]:hidden" onPointerDownOutside={(e) => left > 0 && e.preventDefault()} onEscapeKeyDown={(e) => left > 0 && e.preventDefault()} data-testid="ws-early-dialog">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-rose-100"><AlertTriangle className="h-8 w-8 text-rose-600" /></div>
        <DialogTitle className="text-center text-lg font-bold text-rose-700">ما وصلت السيارة لحد تغيير الزيت</DialogTitle>
        <DialogDescription asChild>
          <div className="space-y-3 text-center text-sm text-slate-600">
            <p>لا يمكن تسجيل تغيير الزيت لهذي السيارة الحين.</p>
            {info && <div className="grid grid-cols-3 gap-2 rounded-xl bg-slate-50 p-3 text-xs">
              <div><div className="text-slate-400">العداد الحالي</div><div className="font-bold text-slate-800" data-testid="ws-early-odo">{n(info.odo)}</div></div>
              <div><div className="text-slate-400">التغيير عند</div><div className="font-bold text-blue-700">{n(info.due)}</div></div>
              <div><div className="text-slate-400">باقي</div><div className="font-bold text-rose-600" data-testid="ws-early-left">{n(info.due - info.odo)} كم</div></div>
            </div>}
            <p className="text-xs text-slate-400">إذا الصيانة قطع غيار بس، اختر «قطع غيار».</p>
          </div>
        </DialogDescription>
        <div><Button onClick={onClose} disabled={left > 0} className="h-11 w-full bg-rose-600 hover:bg-rose-700" data-testid="ws-early-close">{left > 0 ? `قراءة التنبيه... (${left})` : "فهمت"}</Button></div>
      </DialogContent>
    </Dialog>
  );
};

const empty = { vehicle: "", kind: "oil", odometer: "", amount: "", parts: "", odo: null, inv: null };

export default function WorkshopPortal() {
  const { token } = useParams();
  const [info, setInfo] = useState(null); const [err, setErr] = useState("");
  const [f, setF] = useState(empty); const [worker, setWorker] = useState(localStorage.getItem("ws_worker") || "");
  const [saving, setSaving] = useState(false); const [done, setDone] = useState(false);
  const [hist, setHist] = useState(null);
  const [tab, setTab] = useState("log");
  const [ordersN, setOrdersN] = useState(0);
  useEffect(() => { api.workshopOrders(token).then((r) => setOrdersN(r.length)).catch(() => {}); }, [token, tab, done]);
  const isEarly = (odo, kind = f.kind) => kind !== "parts" && hist?.next_due && Number(odo) > 0 && Number(odo) < hist.next_due;
  const early = isEarly(f.odometer);
  const [warn, setWarn] = useState(null);
  const checkEarly = (odo, kind) => { if (isEarly(odo, kind)) setWarn({ odo: Number(odo), due: hist.next_due }); };
  const up = (p) => setF((s) => ({ ...s, ...p }));
  useEffect(() => { api.workshopInfo(token).then(setInfo).catch((e) => setErr(e?.response?.data?.detail || "الرابط غير صالح")); }, [token]);

  const submit = async () => {
    if (!f.vehicle) return toast.error("اختر المركبة");
    if (early) return checkEarly(f.odometer);
    if (!f.odo || !f.inv) return toast.error("أرفق صورة العداد والفاتورة");
    if (!(Number(f.odometer) > 0) || !(Number(f.amount) > 0)) return toast.error("أدخل قراءة العداد ومبلغ الفاتورة");
    if (f.kind !== "oil" && !f.parts.trim()) return toast.error("اكتب القطع اللي تم تغييرها");
    setSaving(true);
    try {
      localStorage.setItem("ws_worker", worker);
      const r = await api.workshopSubmit(token, { vehicle_number: f.vehicle, kind: f.kind, odometer: Number(f.odometer), amount: Number(f.amount), parts: f.parts, worker, odo_file_id: f.odo.file_id, inv_file_id: f.inv.file_id });
      if (r?.released) toast.success("أُغلق أمر الورشة وعادت المركبة للخدمة");
      setDone(true);
    } catch (e) {
      const d = e?.response?.data?.detail || "تعذّر الحفظ";
      if (/الحد المسموح/.test(d) && hist?.next_due) setWarn({ odo: Number(f.odometer), due: hist.next_due }); else toast.error(d);
    }
    finally { setSaving(false); }
  };

  if (err) return <div dir="rtl" className="flex min-h-screen items-center justify-center p-6 text-center text-slate-500" data-testid="ws-error">{err}</div>;
  if (!info) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  return (
    <div dir="rtl" className="min-h-screen bg-slate-50 pb-10" data-testid="workshop-portal">
      <header className="flex items-center gap-3 border-b border-slate-200 bg-white px-5 py-4">
        {info.has_logo ? <img src={`${API_BASE}/public/org/${info.org_id}/logo`} alt="" className="h-11 w-11 rounded-xl object-contain" /> : <Wrench className="h-9 w-9 text-blue-600" />}
        <div><div className="font-heading text-lg font-bold" data-testid="ws-org-name">{info.name}</div><div className="text-xs text-slate-500">بوابة الورشة · صيانة السيارات</div></div>
      </header>
      <main className="mx-auto max-w-lg space-y-5 px-4 pt-5">
        <div className="grid grid-cols-3 gap-1 rounded-2xl bg-slate-200/70 p-1 text-sm" data-testid="ws-tabs">
          {[["log", "تسجيل صيانة"], ["receive", "استلام مركبة"], ["orders", `في الورشة (${ordersN})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`ws-tab-${k}`}
              className={`rounded-xl py-2 font-semibold transition-colors ${tab === k ? "bg-white text-blue-700 shadow-sm" : "text-slate-600"}`}>{l}</button>))}
        </div>
        {tab === "receive" ? <WsReceive Pick={VehiclePick} token={token} worker={worker} setWorker={setWorker} onDone={() => setTab("orders")} />
        : tab === "orders" ? <WsOrders token={token} worker={worker} onCount={setOrdersN} onLog={(vn) => { setF({ ...empty, vehicle: vn }); setDone(false); setTab("log"); }} />
        : done ? (
          <div className="space-y-4 rounded-3xl border border-emerald-200 bg-white p-8 text-center" data-testid="ws-done">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
            <div className="text-lg font-bold">تم تسجيل الصيانة بنجاح</div>
            <div className="text-sm text-slate-500">المركبة {f.vehicle} · {Number(f.amount).toLocaleString("en")} ر.س</div>
            <Button onClick={() => { setF(empty); setDone(false); }} className="w-full" data-testid="ws-new">تسجيل صيانة جديدة</Button>
          </div>
        ) : (<>
          <section className="space-y-2"><div className="text-sm font-bold">1. المركبة</div><VehiclePick token={token} value={f.vehicle} onChange={(v) => up({ vehicle: v })} />{f.vehicle && <History token={token} vn={f.vehicle} onLoad={setHist} />}</section>
          <section className="space-y-2"><div className="text-sm font-bold">2. نوع الصيانة</div>
            <div className="grid grid-cols-3 gap-2">{KINDS.map(([k, l]) => (
              <button key={k} onClick={() => { up({ kind: k }); checkEarly(f.odometer, k); }} className={`rounded-xl border py-2.5 text-sm font-semibold transition-colors ${f.kind === k ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600"}`} data-testid={`ws-kind-${k}`}>{l}</button>
            ))}</div>
            {f.kind !== "oil" && <Input value={f.parts} onChange={(e) => up({ parts: e.target.value })} placeholder="القطع اللي تم تغييرها (مثال: فحمات + فلتر هواء)" data-testid="ws-parts" />}
          </section>
          <section className="space-y-2"><div className="text-sm font-bold">3. الصور</div>
            <div className="grid grid-cols-2 gap-3">
              <PhotoBox label="صورة العداد" icon={Gauge} kind="odometer" token={token} file={f.odo} testid="ws-odo-photo" onDone={(r) => { up({ odo: r, ...(r.value != null ? { odometer: r.value } : {}) }); if (r.value != null) checkEarly(r.value); }} />
              <PhotoBox label="صورة الفاتورة" icon={FileText} kind="invoice" token={token} file={f.inv} testid="ws-inv-photo" onDone={(r) => up({ inv: r, ...(r.value != null ? { amount: r.value } : {}) })} />
            </div>
          </section>
          <section className="grid grid-cols-2 gap-3">
            <div><div className="mb-1 text-xs text-slate-500">قراءة العداد (كم)</div><Input type="number" inputMode="numeric" value={f.odometer} onChange={(e) => up({ odometer: e.target.value })} onBlur={(e) => checkEarly(e.target.value)} className="h-11" data-testid="ws-odometer" /></div>
            <div><div className="mb-1 text-xs text-slate-500">مبلغ الفاتورة (ر.س)</div><Input type="number" inputMode="decimal" value={f.amount} onChange={(e) => up({ amount: e.target.value })} className="h-11" data-testid="ws-amount" /></div>
          </section>
          {early && <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs font-semibold text-rose-700" data-testid="ws-early-warning">ما وصلت السيارة لحد تغيير الزيت. باقي {(hist.next_due - Number(f.odometer)).toLocaleString("en")} كم (التغيير عند {hist.next_due.toLocaleString("en")} كم)، فما راح يُقبل تغيير الزيت.</div>}
          <div><div className="mb-1 text-xs text-slate-500">اسم الفني / المسؤول (اختياري)</div><Input value={worker} onChange={(e) => setWorker(e.target.value)} data-testid="ws-worker" /></div>
          <Button onClick={submit} disabled={saving} className="h-12 w-full gap-2 text-base" data-testid="ws-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} إرسال الصيانة</Button>
        </>)}
      </main>
      <EarlyDialog info={warn} onClose={() => setWarn(null)} />
    </div>
  );
}
